import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import BlockRoundedIcon from "@mui/icons-material/BlockRounded";
import { formatPersonName } from "../../../utils/formatPersonName";
import { buildAdminWhatsAppUrl, openAdminWhatsApp } from "../../../utils/adminWhatsApp";

const formatCurrencyBR = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "N/A";

  return amount.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
};

const parseCurrencyInput = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return Number.NaN;

  const normalized = raw.includes(",")
    ? raw.replace(/\./g, "").replace(",", ".")
    : raw;

  return Number(normalized);
};

const formatDateTimeBR = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "N/A";

  return date.toLocaleString("pt-BR", {
    timeZone: "America/Sao_Paulo",
    hour12: false,
  });
};

const calculateRefundDueDate = (refundRequestedAt, refundDueDays) => {
  const days = Number(refundDueDays);
  if (!refundRequestedAt || !Number.isFinite(days) || days < 0) return null;

  const date = new Date(refundRequestedAt);
  if (Number.isNaN(date.getTime())) return null;

  date.setDate(date.getDate() + days);
  return date;
};

const getDayDiff = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const dueDay = new Date(date);
  dueDay.setHours(0, 0, 0, 0);

  return Math.round((dueDay.getTime() - today.getTime()) / 86400000);
};

const getRefundMeta = (refundRequestedAt, refundDueDays) => {
  const dueDate = calculateRefundDueDate(refundRequestedAt, refundDueDays);
  const diffDays = getDayDiff(dueDate);

  if (!dueDate) {
    return {
      dueDate: null,
      diffDays: null,
      dueLabel: "Sem prazo definido",
      statusLabel: "Definir prazo",
      tone: "neutral",
      statusColor: "default",
      accent: "rgba(255,255,255,0.14)",
      bg: "rgba(255,255,255,0.04)",
    };
  }

  if (diffDays < 0) {
    return {
      dueDate,
      diffDays,
      dueLabel: formatDateTimeBR(dueDate),
      statusLabel: "Atrasado",
      tone: "critical",
      statusColor: "error",
      accent: "rgba(244,67,54,0.34)",
      bg: "rgba(244,67,54,0.12)",
    };
  }

  if (diffDays === 0) {
    return {
      dueDate,
      diffDays,
      dueLabel: formatDateTimeBR(dueDate),
      statusLabel: "Hoje",
      tone: "today",
      statusColor: "warning",
      accent: "rgba(255,183,77,0.34)",
      bg: "rgba(255,152,0,0.14)",
    };
  }

  if (diffDays <= 2) {
    return {
      dueDate,
      diffDays,
      dueLabel: formatDateTimeBR(dueDate),
      statusLabel: `${diffDays} dia(s)`,
      tone: "soon",
      statusColor: "warning",
      accent: "rgba(255,213,79,0.3)",
      bg: "rgba(255,193,7,0.12)",
    };
  }

  return {
    dueDate,
    diffDays,
    dueLabel: formatDateTimeBR(dueDate),
    statusLabel: `${diffDays} dia(s)`,
    tone: "scheduled",
    statusColor: "success",
    accent: "rgba(102,187,106,0.3)",
    bg: "rgba(46,125,50,0.12)",
  };
};

const getPaymentMethodMeta = (method) => {
  const normalized = String(method || "").toUpperCase();

  if (normalized === "PIX") {
    return {
      label: "Pix",
      reserveLabel: "Exige saldo em conta",
      reserveRequired: true,
      color: "info",
    };
  }

  if (normalized === "CREDIT_CARD") {
    return {
      label: "Cartão",
      reserveLabel: "Não exige saldo em conta",
      reserveRequired: false,
      color: "default",
    };
  }

  if (!normalized) {
    return {
      label: "N/A",
      reserveLabel: "Sem método identificado",
      reserveRequired: false,
      color: "default",
    };
  }

  return {
    label: normalized,
    reserveLabel: "Verificar método",
    reserveRequired: false,
    color: "default",
  };
};

const getQueueSummary = (items) =>
  items.reduce(
    (acc, item) => {
      acc.total += 1;
      if (item.access_hold_refund_status !== "completed") {
        if (item.refundMeta.tone === "critical") acc.overdue += 1;
        if (item.refundMeta.tone === "today") acc.today += 1;
        if (item.refundMeta.tone === "soon") acc.soon += 1;
      }

      if (
        item.paymentMethodMeta.reserveRequired &&
        item.access_hold_refund_status !== "completed"
      ) {
        acc.pixReserve += Number(item.refund_payment_amount || 0);
        acc.pixCount += 1;
      }

      return acc;
    },
    { total: 0, overdue: 0, today: 0, soon: 0, pixReserve: 0, pixCount: 0 }
  );

const getWhatsAppLink = (phone) => {
  return buildAdminWhatsAppUrl(phone);
};

const getRefundExecutionMeta = (status, errorMessage, isAccessBlocked = false) => {
  const normalized = String(status || "pending").toLowerCase();

  if (normalized === "completed") {
    return {
      label: isAccessBlocked ? "Reembolsado e bloqueado" : "Reembolsado",
      color: "error",
      helper: isAccessBlocked
        ? "Estorno confirmado no Asaas e acesso bloqueado automaticamente."
        : "Estorno confirmado no Asaas.",
    };
  }

  if (normalized === "requested") {
    return {
      label: "Solicitado",
      color: "info",
      helper: "Estorno enviado automaticamente, aguardando atualização final.",
    };
  }

  if (normalized === "in_progress") {
    return {
      label: "Em processamento",
      color: "warning",
      helper: "Asaas informou que o estorno está em processamento.",
    };
  }

  if (normalized === "denied") {
    return {
      label: "Negado",
      color: "error",
      helper: errorMessage || "O Asaas negou o estorno automático.",
    };
  }

  if (normalized === "failed") {
    return {
      label: "Falhou",
      color: "error",
      helper: errorMessage || "Falha ao solicitar o estorno automático.",
    };
  }

  return {
    label: "Agendado",
    color: "default",
    helper: "O sistema vai solicitar o estorno quando chegar o dia.",
  };
};

const MiniStat = ({ label, value, color }) => (
  <Chip
    label={`${label}: ${value}`}
    size="small"
    sx={{
      color: "#F6FAFF",
      bgcolor: color,
      border: "1px solid rgba(255,255,255,0.08)",
      fontWeight: 700,
      height: 32,
    }}
  />
);

const InfoPill = ({ label, value, action = null }) => (
  <Box
    sx={{
      px: 1.2,
      py: 1,
      borderRadius: 2,
      bgcolor: "rgba(255,255,255,0.04)",
      border: "1px solid rgba(255,255,255,0.06)",
    }}
  >
    <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: "0.7rem", mb: 0.25 }}>
      {label}
    </Typography>
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
      <Typography sx={{ color: "#F5F9FD", fontSize: "0.88rem", fontWeight: 600 }}>
        {value}
      </Typography>
      {action}
    </Box>
  </Box>
);

const AccessHoldQueueModal = ({ open, onClose, onUserDeleted }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [items, setItems] = useState([]);
  const [refundReserve, setRefundReserve] = useState({
    reserveAmount: 0,
    asaasBalance: null,
    availableForRepasse: null,
    balanceError: null,
  });
  const [refundReserveInput, setRefundReserveInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [savingRefundReserve, setSavingRefundReserve] = useState(false);
  const [deletingUserId, setDeletingUserId] = useState(null);
  const [requestingRefundUserId, setRequestingRefundUserId] = useState(null);
  const [pendingDeleteItem, setPendingDeleteItem] = useState(null);

  const loadQueue = async (cancelled = false) => {
    setLoading(true);
    setError("");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/users/access-holds`
      );
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.message || "Erro ao carregar fila de travas.");
      }

      if (!cancelled) {
        const nextItems = Array.isArray(data)
          ? data
          : Array.isArray(data?.items)
          ? data.items
          : [];
        const nextRefundReserve =
          !Array.isArray(data) && data?.refundReserve
            ? data.refundReserve
            : {
                reserveAmount: 0,
                asaasBalance: null,
                availableForRepasse: null,
                balanceError: null,
              };

        setItems(nextItems);
        setRefundReserve(nextRefundReserve);
        setRefundReserveInput(String(nextRefundReserve.reserveAmount ?? 0));
      }
    } catch (err) {
      if (!cancelled) {
        setError(err?.message || "Erro ao carregar fila de travas.");
      }
    } finally {
      if (!cancelled) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!open) return;

    let cancelled = false;

    loadQueue(cancelled);

    return () => {
      cancelled = true;
    };
  }, [open]);

  const decoratedItems = useMemo(() => {
    return [...items]
      .map((item) => ({
        ...item,
        refundMeta: getRefundMeta(
          item.access_hold_refund_requested_at,
          item.access_hold_refund_due_days
        ),
        paymentMethodMeta: getPaymentMethodMeta(item.refund_payment_method),
        refundExecutionMeta: getRefundExecutionMeta(
          item.access_hold_refund_status,
          item.access_hold_refund_last_error,
          Number(item.is_paid) === 0
        ),
      }))
      .sort((a, b) => {
        const aCompleted = a.access_hold_refund_status === "completed";
        const bCompleted = b.access_hold_refund_status === "completed";

        if (aCompleted !== bCompleted) {
          return aCompleted ? 1 : -1;
        }

        const aTime = a.refundMeta.dueDate?.getTime() ?? Number.POSITIVE_INFINITY;
        const bTime = b.refundMeta.dueDate?.getTime() ?? Number.POSITIVE_INFINITY;
        return aTime - bTime;
      });
  }, [items]);

  const summary = useMemo(() => getQueueSummary(decoratedItems), [decoratedItems]);

  const handleSaveRefundReserve = async () => {
    if (savingRefundReserve) return;

    const amount = parseCurrencyInput(refundReserveInput);
    if (!Number.isFinite(amount) || amount < 0) {
      setError("Informe um valor válido para a trava de reembolso.");
      return;
    }

    setSavingRefundReserve(true);
    setError("");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/users/access-holds/refund-reserve`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ amount }),
        }
      );
      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(data?.message || "Erro ao atualizar trava de reembolso.");
      }

      if (data?.refundReserve) {
        setRefundReserve(data.refundReserve);
        setRefundReserveInput(String(data.refundReserve.reserveAmount ?? amount));
      }
    } catch (err) {
      setError(err?.message || "Erro ao atualizar trava de reembolso.");
    } finally {
      setSavingRefundReserve(false);
    }
  };

  const handleRefundDone = async (item) => {
    if (!item?.id || deletingUserId) return;

    setDeletingUserId(item.id);
    setError("");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/users/delete-user/${item.id}`,
        { method: "DELETE" }
      );
      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(data?.message || "Erro ao excluir usuário.");
      }

      setItems((prev) => prev.filter((entry) => entry.id !== item.id));
      if (typeof onUserDeleted === "function") {
        onUserDeleted(item.id);
      }
    } catch (err) {
      setError(err?.message || "Erro ao excluir usuário.");
    } finally {
      setDeletingUserId(null);
    }
  };

  const handleAdvanceRefund = async (item) => {
    if (!item?.id || requestingRefundUserId || deletingUserId) return;

    setRequestingRefundUserId(item.id);
    setError("");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/users/${item.id}/refund-latest-payment`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({}),
        }
      );
      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(data?.message || "Erro ao adiantar reembolso.");
      }

      await loadQueue(false);
    } catch (err) {
      setError(err?.message || "Erro ao adiantar reembolso.");
    } finally {
      setRequestingRefundUserId(null);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          bgcolor: "#111827",
          backgroundImage:
            "linear-gradient(180deg, rgba(17,24,39,0.98) 0%, rgba(11,16,24,0.99) 100%)",
        },
      }}
    >
      <DialogTitle
        sx={{
          px: isMobile ? 2 : 2.5,
          py: 1.8,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          borderBottom: "1px solid rgba(255,255,255,0.06)",
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 900, fontSize: "1.15rem", color: "#F8FBFF" }}>
            Fila de Reembolsos
          </Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.62)", fontSize: "0.88rem", mt: 0.35 }}>
            Veja rápido quem precisa de ação e em qual dia.
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: "#EAF2FB" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: isMobile ? 2 : 2.5, py: 2 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : decoratedItems.length === 0 ? (
          <Alert severity="info">Nenhuma trava ativa no momento.</Alert>
        ) : (
          <Stack spacing={1.4}>
            <Box sx={{ display: "flex", gap: 0.8, flexWrap: "wrap" }}>
              <MiniStat label="Total" value={summary.total} color="rgba(66,165,245,0.18)" />
              <MiniStat label="Atrasados" value={summary.overdue} color="rgba(244,67,54,0.18)" />
              <MiniStat label="Hoje" value={summary.today} color="rgba(255,152,0,0.18)" />
              <MiniStat label="Próximos" value={summary.soon} color="rgba(255,193,7,0.18)" />
            </Box>

            <Box
              sx={{
                p: 1.2,
                borderRadius: 2.5,
                bgcolor: "rgba(34,197,94,0.08)",
                border: "1px solid rgba(34,197,94,0.18)",
              }}
            >
              <Typography
                sx={{
                  color: "rgba(255,255,255,0.58)",
                  fontSize: "0.72rem",
                  textTransform: "uppercase",
                  letterSpacing: 0.8,
                  mb: 0.4,
                  fontWeight: 700,
                }}
              >
                Saldo necessário para reembolsos PIX
              </Typography>
              <Typography
                sx={{
                  color: "#F8FBFF",
                  fontSize: isMobile ? "1.05rem" : "1.18rem",
                  fontWeight: 900,
                  lineHeight: 1.2,
                }}
              >
                {formatCurrencyBR(summary.pixReserve)}
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.58)", fontSize: "0.8rem", mt: 0.45 }}>
                {summary.pixCount > 0
                  ? `${summary.pixCount} reembolso(s) via Pix exigem saldo disponível na conta.`
                  : "No momento não há reembolso via Pix exigindo reserva em conta."}
              </Typography>
            </Box>

            <Box
              sx={{
                p: 1.2,
                borderRadius: 2.5,
                bgcolor: "rgba(59,130,246,0.08)",
                border: "1px solid rgba(59,130,246,0.18)",
              }}
            >
              <Typography
                sx={{
                  color: "rgba(255,255,255,0.58)",
                  fontSize: "0.72rem",
                  textTransform: "uppercase",
                  letterSpacing: 0.8,
                  mb: 0.4,
                  fontWeight: 700,
                }}
              >
                Trava dinâmica de reembolso
              </Typography>
              <Typography
                sx={{
                  color: "#F8FBFF",
                  fontSize: isMobile ? "1.05rem" : "1.18rem",
                  fontWeight: 900,
                  lineHeight: 1.2,
                }}
              >
                {formatCurrencyBR(refundReserve.reserveAmount)}
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.58)", fontSize: "0.8rem", mt: 0.45 }}>
                Quando o Asaas confirma um reembolso, essa trava é reduzida automaticamente.
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                  gap: 0.8,
                  mt: 1.15,
                }}
              >
                <InfoPill
                  label="Saldo Asaas"
                  value={formatCurrencyBR(refundReserve.asaasBalance)}
                />
                <InfoPill
                  label="Livre para repasse"
                  value={formatCurrencyBR(refundReserve.availableForRepasse)}
                />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: isMobile ? "column" : "row",
                  gap: 0.9,
                  mt: 1.15,
                  alignItems: isMobile ? "stretch" : "center",
                }}
              >
                <TextField
                  fullWidth
                  size="small"
                  label="Definir trava manual"
                  value={refundReserveInput}
                  onChange={(event) => setRefundReserveInput(event.target.value)}
                  placeholder="251,00"
                  inputProps={{ inputMode: "decimal" }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      color: "#F8FBFF",
                      bgcolor: "rgba(255,255,255,0.03)",
                    },
                    "& .MuiInputLabel-root": {
                      color: "rgba(255,255,255,0.58)",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(255,255,255,0.12)",
                    },
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleSaveRefundReserve}
                  disabled={savingRefundReserve}
                  sx={{
                    minWidth: isMobile ? "100%" : 148,
                    minHeight: 40,
                    fontWeight: 800,
                    textTransform: "none",
                  }}
                >
                  {savingRefundReserve ? "Salvando..." : "Salvar trava"}
                </Button>
              </Box>

              {refundReserve.balanceError ? (
                <Typography
                  sx={{ color: "#FCA5A5", fontSize: "0.78rem", mt: 0.9, lineHeight: 1.45 }}
                >
                  {refundReserve.balanceError}
                </Typography>
              ) : null}
            </Box>

            {decoratedItems.map((item) => (
              <Box
                key={`${item.id}-${item.access_hold_created_at || "hold"}`}
                sx={{
                  p: 1.35,
                  borderRadius: 3,
                  background:
                    item.access_hold_refund_status === "completed"
                      ? "linear-gradient(180deg, rgba(33,10,14,0.98) 0%, rgba(51,12,18,0.98) 100%)"
                      : "#0F172A",
                  border:
                    item.access_hold_refund_status === "completed"
                      ? "1px solid rgba(248, 113, 113, 0.24)"
                      : `1px solid ${item.refundMeta.accent}`,
                  boxShadow:
                    item.access_hold_refund_status === "completed"
                      ? "0 16px 30px rgba(28, 5, 8, 0.28)"
                      : "0 12px 28px rgba(0,0,0,0.18)",
                }}
              >
                {item.access_hold_refund_status === "completed" && (
                  <Box
                    sx={{
                      mb: 1,
                      px: 1.1,
                      py: 0.9,
                      borderRadius: 2.3,
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "center",
                      gap: 0.9,
                      bgcolor: "rgba(127, 29, 29, 0.34)",
                      border: "1px solid rgba(248, 113, 113, 0.18)",
                    }}
                  >
                    <TaskAltRoundedIcon sx={{ color: "#FFD3D3", fontSize: 18 }} />
                    <Typography
                      sx={{
                        color: "#FFF2F2",
                        fontSize: "0.78rem",
                        fontWeight: 900,
                        letterSpacing: 0.4,
                        textTransform: "uppercase",
                      }}
                    >
                      Caso encerrado
                    </Typography>
                    {Number(item.is_paid) === 0 && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          px: 0.9,
                          py: 0.3,
                          borderRadius: "999px",
                          bgcolor: "rgba(239, 68, 68, 0.22)",
                          color: "#FFE1E1",
                          fontSize: "0.72rem",
                          fontWeight: 800,
                        }}
                      >
                        <BlockRoundedIcon sx={{ fontSize: 14 }} />
                        acesso bloqueado
                      </Box>
                    )}
                  </Box>
                )}

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 1,
                    mb: 1.1,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                      <WarningAmberRoundedIcon
                        sx={{
                          color:
                            item.access_hold_refund_status === "completed"
                              ? "#FCA5A5"
                              : "#F59E0B",
                          fontSize: 20,
                        }}
                      />
                      <Typography
                        sx={{
                          color: "#F8FBFF",
                          fontWeight: 800,
                          fontSize: "1rem",
                          lineHeight: 1.25,
                        }}
                      >
                        {formatPersonName(item.full_name) || "Usuário sem nome"}
                      </Typography>
                    </Box>
                    <Typography sx={{ color: "rgba(255,255,255,0.58)", fontSize: "0.82rem", mt: 0.55 }}>
                      {item.access_hold_refund_status === "completed"
                        ? `Reembolsado em ${formatDateTimeBR(item.access_hold_refund_processed_at)}`
                        : `Pedido em ${formatDateTimeBR(item.access_hold_refund_requested_at)}`}
                    </Typography>
                  </Box>

                  <Chip
                    label={item.refundMeta.statusLabel}
                    color={item.refundMeta.statusColor}
                    variant={item.refundMeta.statusColor === "default" ? "outlined" : "filled"}
                    size="small"
                    sx={{ fontWeight: 800, flexShrink: 0 }}
                  />
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mb: 1 }}>
                  <Chip
                    label={item.refundExecutionMeta.label}
                    color={item.refundExecutionMeta.color}
                    size="small"
                    variant={item.refundExecutionMeta.color === "default" ? "outlined" : "filled"}
                    sx={{ fontWeight: 800 }}
                  />
                  <Typography sx={{ color: "rgba(255,255,255,0.58)", fontSize: "0.78rem" }}>
                    {item.refundExecutionMeta.helper}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    p: 1.2,
                    borderRadius: 2.5,
                    bgcolor:
                      item.access_hold_refund_status === "completed"
                        ? "rgba(127, 29, 29, 0.22)"
                        : item.refundMeta.bg,
                    border:
                      item.access_hold_refund_status === "completed"
                        ? "1px solid rgba(248, 113, 113, 0.16)"
                        : `1px solid ${item.refundMeta.accent}`,
                    mb: 1.1,
                  }}
                >
                  <Typography
                    sx={{
                      color: "rgba(255,255,255,0.58)",
                      fontSize: "0.72rem",
                      textTransform: "uppercase",
                      letterSpacing: 0.8,
                      mb: 0.45,
                      fontWeight: 700,
                    }}
                  >
                    {item.access_hold_refund_status === "completed"
                      ? "Reembolso concluído"
                      : "Reembolso programado"}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                    <ScheduleRoundedIcon
                      sx={{
                        color:
                          item.access_hold_refund_status === "completed"
                            ? "#FFD0D0"
                            : "#F8FBFF",
                        fontSize: 18,
                      }}
                    />
                    <Typography
                      sx={{
                        color: "#FFFFFF",
                        fontWeight: 900,
                        fontSize: isMobile ? "1rem" : "1.15rem",
                        lineHeight: 1.2,
                      }}
                    >
                      {item.access_hold_refund_status === "completed"
                        ? formatDateTimeBR(item.access_hold_refund_processed_at)
                        : item.refundMeta.dueLabel}
                    </Typography>
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                    gap: 0.8,
                    mb: 1.1,
                  }}
                >
                  <InfoPill
                    label="Valor do reembolso"
                    value={formatCurrencyBR(item.refund_payment_amount)}
                  />
                  <InfoPill
                    label="Pagamento via"
                    value={item.paymentMethodMeta.label}
                  />
                </Box>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                    gap: 0.8,
                    mb: 1.1,
                  }}
                >
                  <InfoPill
                    label="Prazo"
                    value={
                      item.access_hold_refund_due_days == null
                        ? "Sem prazo"
                        : `${item.access_hold_refund_due_days} dia(s)`
                    }
                  />
                  <InfoPill
                    label="Telefone"
                    value={item.phone || "N/A"}
                    action={
                      getWhatsAppLink(item.phone) ? (
                        <IconButton
                          onClick={() => openAdminWhatsApp(item.phone)}
                          size="small"
                          sx={{
                            color: "#25D366",
                            bgcolor: "rgba(37,211,102,0.12)",
                            border: "1px solid rgba(37,211,102,0.22)",
                            "&:hover": {
                              bgcolor: "rgba(37,211,102,0.2)",
                            },
                          }}
                        >
                          <WhatsAppIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      ) : null
                    }
                  />
                </Box>

                <InfoPill label="E-mail" value={item.email || "N/A"} />

                <Typography
                  sx={{
                    color: "rgba(255,255,255,0.54)",
                    fontSize: "0.78rem",
                    lineHeight: 1.45,
                    mt: 1.05,
                  }}
                >
                  {item.paymentMethodMeta.reserveLabel}
                </Typography>

                {item.access_hold_reason ? (
                  <Typography
                    sx={{
                      color: "rgba(255,255,255,0.54)",
                      fontSize: "0.79rem",
                      lineHeight: 1.5,
                      mt: 0.85,
                    }}
                  >
                    {item.access_hold_reason}
                  </Typography>
                ) : null}

                <Button
                  fullWidth
                  variant="outlined"
                  color="warning"
                  onClick={() => handleAdvanceRefund(item)}
                  disabled={
                    requestingRefundUserId === item.id ||
                    deletingUserId === item.id ||
                    item.access_hold_refund_status === "completed" ||
                    item.access_hold_refund_status === "requested" ||
                    item.access_hold_refund_status === "in_progress"
                  }
                  sx={{
                    mt: 1.2,
                    borderRadius: 2.4,
                    minHeight: 42,
                    fontWeight: 800,
                    textTransform: "none",
                  }}
                >
                  {requestingRefundUserId === item.id
                    ? "Solicitando estorno..."
                    : item.access_hold_refund_status === "failed" ||
                      item.access_hold_refund_status === "denied"
                    ? "Tentar reembolso novamente"
                    : "Adiantar reembolso"}
                </Button>

                <Button
                  fullWidth
                  variant="contained"
                  color={item.access_hold_refund_status === "completed" ? "error" : "inherit"}
                  onClick={() => setPendingDeleteItem(item)}
                  disabled={
                    deletingUserId === item.id ||
                    requestingRefundUserId === item.id ||
                    item.access_hold_refund_status !== "completed"
                  }
                  sx={{
                    mt: 1,
                    borderRadius: 2.4,
                    minHeight: 44,
                    fontWeight: 800,
                    textTransform: "none",
                    boxShadow:
                      item.access_hold_refund_status === "completed"
                        ? "0 12px 24px rgba(185,28,28,0.24)"
                        : "none",
                  }}
                >
                  {deletingUserId === item.id
                    ? "Excluindo..."
                    : item.access_hold_refund_status === "completed"
                    ? "Excluir usuário"
                    : "Aguardando reembolso"}
                </Button>
              </Box>
            ))}
          </Stack>
        )}
      </DialogContent>

      <Dialog
        open={Boolean(pendingDeleteItem)}
        onClose={() => {
          if (!deletingUserId) {
            setPendingDeleteItem(null);
          }
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirmar reembolso</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {pendingDeleteItem
              ? `O usuário ${formatPersonName(
                  pendingDeleteItem.full_name
                )} será excluído do sistema após confirmar que o reembolso foi feito. Deseja continuar?`
              : ""}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setPendingDeleteItem(null)}
            disabled={Boolean(deletingUserId)}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={Boolean(deletingUserId) || !pendingDeleteItem}
            onClick={async () => {
              if (!pendingDeleteItem) return;
              await handleRefundDone(pendingDeleteItem);
              setPendingDeleteItem(null);
            }}
          >
            {deletingUserId ? "Excluindo..." : "Excluir usuário"}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default AccessHoldQueueModal;
